import React, { useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ShieldAlert, History, Check } from 'lucide-react';
import { toast } from 'sonner';

export function DataRecoveryCard() {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get('/admin/recover-wiped-fields/preview');
      setPreview(data);
      if (data.total_changes === 0) {
        toast.info('Aucun champ à restaurer — votre base est saine');
      } else {
        toast.success(`${data.total_changes} champs récupérables sur ${data.affected_prospects} fiches`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de l\'aperçu'); }
    setPreviewing(false);
  };

  const doApply = async () => {
    if (!preview || preview.total_changes === 0) return;
    const msg = `Restaurer ${preview.total_changes} valeurs sur ${preview.affected_prospects} fiches ? Cette action est tracée mais irréversible.`;
    if (!window.confirm(msg)) return;
    setApplying(true);
    try {
      const { data } = await api.post('/admin/recover-wiped-fields/apply');
      toast.success(`${data.applied_fields} champs restaurés sur ${data.applied_prospects} fiches ✓`);
      setPreview(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la restauration'); }
    setApplying(false);
  };

  return (
    <Card className="border-[#FCA5A5] bg-[#FEF2F2]" data-testid="data-recovery-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-[#B91C1C] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-base font-bold text-[#7F1D1D]">Récupération de données effacées</h3>
            <p className="text-sm text-[#991B1B] mt-1">
              Utilise le journal d'activités pour restaurer les valeurs des champs qui auraient été
              effacés par bug (enseigne, magasin, dirigeant, adresse, etc.). Sécurisé : ne restaure
              que les champs actuellement vides.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={doPreview} disabled={previewing}
            variant="outline" className="border-[#B91C1C] text-[#B91C1C] hover:bg-[#FEE2E2]"
            data-testid="recovery-preview-btn">
            {previewing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <History className="w-4 h-4 mr-1" />}
            Aperçu de la restauration
          </Button>
          {preview && preview.total_changes > 0 && (
            <Button onClick={doApply} disabled={applying}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
              data-testid="recovery-apply-btn">
              {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Restaurer maintenant ({preview.total_changes} champs)
            </Button>
          )}
        </div>

        {preview && (
          <div className="text-xs bg-white border border-[#FCA5A5] rounded p-3 space-y-2 max-h-72 overflow-y-auto">
            <div className="flex flex-wrap gap-3 text-[#7F1D1D] font-medium">
              <span>{preview.total_changes} champs à restaurer</span>
              <span>·</span>
              <span>{preview.affected_prospects} fiches concernées</span>
            </div>
            {Object.keys(preview.by_field || {}).length > 0 && (
              <div className="text-[#71717A]">
                Ventilation : {Object.entries(preview.by_field).map(([k, v]) => `${k} (${v})`).join(', ')}
              </div>
            )}
            {(preview.sample || []).length > 0 && (
              <div className="space-y-1 pt-1 border-t border-[#FCA5A5]">
                <p className="font-semibold text-[#7F1D1D]">Aperçu (25 premiers) :</p>
                {preview.sample.map((s, i) => (
                  <div key={i} className="flex flex-wrap gap-2 py-0.5">
                    <span className="font-mono text-[10px] text-[#71717A]">{s.prospect_id.slice(0, 8)}</span>
                    <span className="font-medium">{s.prospect_label}</span>
                    <span className="text-[#71717A]">·</span>
                    <span className="text-[#B91C1C]">{s.field}</span>
                    <span className="text-[#71717A]">→</span>
                    <span className="text-[#15803D]">"{s.restore_to.slice(0, 50)}"</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
